import React, { useEffect, useMemo, useState } from 'react';
import { PageJumpPagination, Spinner } from '../../components/common';
import Header from '../../components/common/global-top-bar';
import { useHistory } from 'react-router';
import Modal from '../../components/common/modal';
import Searchbar from '../../components/common/searchbar';
import ReactTooltip from 'react-tooltip';
import moment from 'moment-timezone';
import { crisis, log, report } from '../../assets/images';
import { convertObjectToParams } from '../../components/common/create-params';
import { ROUTE_PATH } from '../../routes/paths';
import { ClientMinimal, useClientMinimalList, useDomains } from '../../hooks/useClients';
import {
  Connection,
  Count,
  TopicTag,
  Event,
  useConnections,
  useConnectionsCount,
  useEvents,
} from '../../hooks/useConnections';
import { convertToHoursMinuteSeconds, connectionOutcomeText, generateResources } from '../../components/connections';
import { TagGroup, use2026TagGroups } from '../../hooks/useTagGroups';
import { Toggle } from '../../components/ui';

const resolveMemberTopicNames = (
  tagGroupIds: number[] | undefined | null,
  legacyTopicName: string | undefined | null,
  tagGroupIdToName: Map<number, string>,
): string[] => {
  const names: string[] = [];
  if (tagGroupIds?.length) {
    // Empty map means the 2026 catalog is still loading — fall back to legacy below.
    // Once loaded, missing IDs (archived / not in tree) show as Unknown topic instead of vanishing.
    const catalogLoaded = tagGroupIdToName.size > 0;
    tagGroupIds.forEach((id) => {
      const name = tagGroupIdToName.get(id);
      if (name) names.push(name);
      else if (catalogLoaded) names.push(`Unknown topic (${id})`);
    });
  }
  if (names.length === 0 && legacyTopicName) {
    names.push(legacyTopicName);
  }
  return names;
};

const buildTagGroupIdToNameMap = (tagGroups?: TagGroup[]): Map<number, string> => {
  const map = new Map<number, string>();
  const addGroup = (group: TagGroup) => {
    map.set(group.id, group.name);
    group.children?.forEach(addGroup);
  };
  tagGroups?.forEach(addGroup);
  return map;
};

const Connections: React.FC = (): JSX.Element => {
  const history = useHistory();
  const timeZone = moment.tz(moment.tz.guess()).zoneName();
  const [enableCallPopUp, setEnableCallPopUp] = useState(false);
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    search?: string;
    clientId?: number;
    domainId?: number;
  }>({
    page: 1,
    limit: 25,
  });
  const { page, limit, search, clientId, domainId } = filters;
  const [callId, setCallId] = useState<number | undefined>(undefined);
  const [requestId, setRequestId] = useState<number | undefined>(undefined);
  const [selectedConnection, setSelectedConnection] = useState<Connection | undefined>(undefined);
  const [testUserState, setTestUserState] = useState<'live' | 'test'>('live');
  const { data: count, refetch: refetchCount } = useConnectionsCount({
    search,
    testUserState,
    clientId,
    domainId,
  });
  const { data: clients } = useClientMinimalList({ statuses: 'active' });
  const { data: domains } = useDomains();
  const { data: tagGroups } = use2026TagGroups();
  const tagGroupIdToName = useMemo(() => buildTagGroupIdToNameMap(tagGroups), [tagGroups]);
  const {
    data: connections,
    isLoading,
    refetch: refetchConnections,
  } = useConnections({ page, limit, search, testUserState, clientId, domainId });
  const { data: callLog } = useEvents(callId, requestId);

  const handlePeer = (peer: any) => {
    history.push(
      ROUTE_PATH.LISTENERS_PROFILE +
        convertObjectToParams({
          listenerRoleId: peer.user.listener_role_id,
          listenerId: peer.user.id,
          listenerType: 'listener',
          listenerName: peer.user.first_name,
        }),
    );
  };
  const handleCaller = (callerRole: any) => {
    history.push(
      ROUTE_PATH.MEMBERS_PROFILE +
        convertObjectToParams({
          name: callerRole.user.first_name,
          id: callerRole.user.id,
          callerRoleId: callerRole.id,
        }),
    );
  };

  const toggleUserState = () => {
    setTestUserState((prev) => (prev === 'live' ? 'test' : 'live'));
  };

  useEffect(() => {
    refetchConnections();
    refetchCount();
  }, [filters, refetchConnections, refetchCount]);

  useEffect(() => {
    if (count == null) return;
    const pageSize = limit > 0 ? limit : 25;
    const pageCount = Math.max(1, Math.ceil(count.count / pageSize));
    if (page > pageCount) {
      setFilters((prev) => ({ ...prev, page: pageCount }));
    }
  }, [count, page, limit]);

  return (
    <div className="flex h-screen min-w-0 flex-col overflow-hidden">
      <div className="shrink-0">
        <Header heading="Calls" />
      </div>
      <Filters
        page={page}
        userState={testUserState}
        toggleUserState={toggleUserState}
        count={count}
        limit={limit}
        clients={clients}
        domains={domains}
        setFilters={setFilters}
      />
      {isLoading && <Spinner alignmentClass={'left-64 top-30 fixed z-100 inset-0 overflow-y-auto'} />}

      {/* One pane owns both axes so the horizontal bar stays on this screen (KINDLY-1367). */}
      <div className="min-h-0 flex-1 overflow-auto px-4 md:px-7">
        <table className="table-row-format relative w-max min-w-full border-collapse whitespace-nowrap text-sm">
          <thead className="h-full bg-white">
            <tr className="w-full border-b border-gray-200 text-left shadow-sm">
              <th className="sticky top-0 z-50 mb-4 h-full w-20 bg-white px-1 py-3 text-right">
                <span className="px-3">Log</span>
              </th>
              <th className="sticky top-0 mb-4 h-full w-8 bg-white py-3"></th>
              <th className="sticky top-0 mb-4 h-full w-44 bg-white py-3">
                <span className="px-2">Peer</span>
              </th>
              <th className="sticky top-0 mb-4 h-full w-44 bg-white py-3">
                <span className="px-2">Member</span>
              </th>
              <th className="sticky top-0 mb-4 h-full w-52 bg-white py-3 text-left">Date/Time</th>
              <th className="sticky top-0 mb-4 h-full w-52 bg-white py-3 text-left">Request Time</th>
              <th className="sticky top-0 mb-4 h-full w-24 bg-white py-3">Length</th>
              <th className="sticky top-0 mb-4 h-full w-24 bg-white py-3">Type</th>
              <th className="sticky top-0 mb-4 h-full w-44 bg-white py-3">Topic</th>
              <th className="sticky top-0 mb-4 h-full w-56 bg-white py-3">Member Note</th>
              <th className="sticky top-0 mb-4 h-full w-56 bg-white py-3">Topics Discussed</th>
              <th className="sticky top-0 mb-4 h-full w-40 bg-white py-3">
                <span className="pl-2">Outcome</span>
              </th>
              <th className="sticky top-0 mb-4 h-full w-40 bg-white py-3">
                <span className="pl-2">Result</span>
              </th>
              <th className="sticky top-0 mb-4 h-full w-36 bg-white py-3">Impact Score</th>
              <th className="sticky top-0 mb-4 h-full w-40 bg-white py-3">Call Reported?</th>
              <th className="sticky top-0 mb-4 h-full w-48 bg-white py-3">Mood (Before/After)</th>
              <th className="sticky top-0 mb-4 h-full w-24 bg-white py-3">Resources</th>
            </tr>
          </thead>
          <tbody>
            <tr className="h-2"></tr>
            {connections &&
              connections.length > 0 &&
              connections.map((connection: Connection) => {
                const callDate = connection?.scheduled_at
                  ? connection.scheduled_at
                  : connection?.created_at && connection?.created_at;
                const startDate = callDate ? moment(callDate).local().format('MMM Do YYYY') : '-';
                const time = callDate ? moment(callDate).format('HH:mm') : '-';
                const peer = connection?.listener_role;
                const callerRole = connection?.request?.caller_role;
                const memberTopics = resolveMemberTopicNames(
                  connection.request?.tag_group_ids,
                  connection.request?.topic?.name,
                  tagGroupIdToName,
                );
                const memberNote = connection.request?.text?.trim();
                return (
                  <tr
                    key={connection.id}
                    className="text-gray-dark h-12 space-y-6 rounded border-4 border-y border-white bg-gray-200 text-left"
                  >
                    <td className=" ">
                      <div className="flex items-center justify-end px-2">
                        <button
                          className="blue-primary h-full rotate-180 transform rounded-full"
                          onClick={() => {
                            connection?.request?.call_id
                              ? setCallId(connection?.request?.call_id)
                              : setRequestId(connection?.request.id);
                            setSelectedConnection(connection);
                            setEnableCallPopUp(true);
                          }}
                        >
                          <img className="h-8 w-full" src={log} alt="call details"></img>
                        </button>
                      </div>
                    </td>
                    <td>{connection?.crisis_elevation_id && <img src={crisis} alt="crisis"></img>}</td>
                    <td
                      className="cursor-pointer pl-2 underline"
                      onClick={() => {
                        handlePeer(peer);
                      }}
                    >
                      {' '}
                      {`${peer?.user?.first_name}`}
                    </td>
                    <td
                      className="cursor-pointer pl-2 underline"
                      onClick={() => {
                        handleCaller(callerRole);
                      }}
                    >
                      {`${callerRole?.user?.first_name}`}
                    </td>
                    <td className=" ">
                      {startDate} / {time} {timeZone}
                    </td>
                    {/* requested at time */}
                    <td className=" ">
                      {moment(connection.request.created_at).local().format('MMM Do YYYY [/] HH:mm')} {timeZone}
                    </td>
                    <td className=" ">
                      {connection?.request?.call
                        ? convertToHoursMinuteSeconds(connection?.request?.call.logged_duration)
                        : `--`}
                    </td>
                    <td className=" ">
                      <button
                        className={
                          'cursor-auto whitespace-nowrap rounded-full px-3 py-2 text-center text-xs text-white' +
                          (connection.scheduled_at ? ' bg-magenta' : ' bg-violet')
                        }
                      >
                        {connection.scheduled_at ? ' Scheduled ' : ' Now '}
                      </button>
                    </td>
                    <td className="px-2 py-1">
                      <MemberTopics topics={memberTopics} />
                    </td>
                    <td className="px-2 py-1">
                      <MemberNote text={memberNote} tooltipId={`member-note-${connection.id}`} />
                    </td>
                    <td className="px-2 py-1">
                      <TopicsDiscussed topics={connection.request.call?.postcall_metadata?.topic_tags} />
                    </td>
                    <td className="px-2 py-1">
                      <button
                        className={
                          'cursor-auto whitespace-nowrap rounded-full px-3 py-2 text-center text-xs text-white ' +
                          (connection.outcome === 'Call Scheduled'
                            ? 'bg-blue-gradient-1'
                            : connection.outcome === 'Call'
                              ? 'bg-green'
                              : connection.outcome === 'Short Call'
                                ? ' bg-magenta'
                                : connection.outcome === 'Passed'
                                  ? ' bg-orange'
                                  : connection.outcome === 'Missed'
                                    ? ' bg-red'
                                    : connection.outcome === 'Pending Response'
                                      ? ' '
                                      : connection.outcome === 'Cancelled'
                                        ? ' bg-red'
                                        : connection.outcome === 'Rescheduled'
                                          ? ' bg-violet'
                                          : ' bg-black')
                        }
                      >
                        {connection.outcome ? connectionOutcomeText(connection.outcome, connection.expired) : '-'}
                      </button>
                    </td>
                    <td className="px-2 py-1">{connection.result ? connection.result : '-'}</td>
                    <td
                      className={
                        'h-full w-full cursor-auto rounded-md px-2 py-2 text-center text-xs font-bold text-black'
                      }
                    >
                      {connection.request?.call?.impact_score ? connection.request.call.impact_score.toFixed(2) : '-'}
                    </td>
                    <td className=" ">
                      {connection.request.call?.postcall_metadata?.call_reported ? (
                        <img src={report} alt={'call_reported'} />
                      ) : (
                        'No'
                      )}
                    </td>
                    <td className=" ">
                      {connection.request.call?.postcall_metadata ? (
                        <>
                          <span className="text-red font-bold">
                            {connection.request.call?.postcall_metadata?.begin_call_mood?.toFixed(2) || 0.0}
                          </span>
                          <span>{' / '}</span>
                          <span className="text-green font-bold">
                            {connection.request.call?.postcall_metadata?.end_call_mood?.toFixed(2) || 0.0}
                          </span>{' '}
                        </>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td className=" ">
                      {connection?.request?.call?.postcall_metadata
                        ? generateResources(connection?.request?.call?.postcall_metadata?.resources)
                        : '--'}
                    </td>
                  </tr>
                );
              })}
            {connections && connections.length === 0 && !isLoading && (
              <tr>
                <td colSpan={17} className="border border-white bg-gray-100 py-12 text-center text-gray-600">
                  No calls match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Modal
        isModel={enableCallPopUp}
        InnerComponent={
          <div className="z-100 fixed inset-0 flex items-center justify-center overflow-x-hidden outline-none focus:outline-none">
            <div className="min-w-3xl max-h-162.5 relative mx-auto my-6 w-auto max-w-5xl overflow-y-auto">
              <div className="relative flex w-full flex-col rounded-lg border-0 bg-white shadow-lg outline-none focus:outline-none">
                <div className="sticky top-0 z-50 flex w-full items-start justify-between bg-white">
                  <div className="blue-primary px-10 py-6 text-xl">
                    {callId ? `Details for Call ${callId}:` : `Details for Request ${requestId}:`}
                  </div>
                  <div className="flex items-center justify-center px-10 py-6">
                    <button
                      className="bg-gray-light rounded-full px-7 py-2 text-center text-sm text-white"
                      type="button"
                      onClick={() => {
                        setEnableCallPopUp(false);
                        setCallId(undefined);
                        setRequestId(undefined);
                        setSelectedConnection(undefined);
                      }}
                    >
                      CLOSE
                    </button>
                  </div>
                </div>
                {selectedConnection?.request && (
                  <div className="text-gray-dark space-y-2 px-10 pb-4 text-sm">
                    <div>
                      <span className="font-bold">Member topic: </span>
                      {resolveMemberTopicNames(
                        selectedConnection.request.tag_group_ids,
                        selectedConnection.request.topic?.name,
                        tagGroupIdToName,
                      ).join(', ') || 'No Topic'}
                    </div>
                    <div>
                      <span className="font-bold">Member note: </span>
                      {selectedConnection.request.text?.trim() || '—'}
                    </div>
                  </div>
                )}
                <table className="table-row-format relative mx-10 table-fixed border-collapse">
                  <thead className="sticky top-20 z-40 h-full bg-white">
                    <tr className="w-full px-7 text-left">
                      <th className="text-gray-dark mb-4 h-full w-6/12 bg-white px-2 py-2 text-base font-normal">
                        TimeStamp
                      </th>
                      <th className="text-gray-dark mb-4 h-full w-6/12 bg-white px-2 py-2 text-base font-normal">
                        Event Category
                      </th>
                      <th className="text-gray-dark mb-4 h-full w-6/12 bg-white px-2 py-2 text-base font-normal">
                        Event name
                      </th>
                      <th className="text-gray-dark mb-4 h-full w-6/12 bg-white px-2 py-2 text-base font-normal">
                        Message
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {callLog && callLog.length > 0 ? (
                      callLog?.map((event: Event) => (
                        <tr
                          role="button"
                          className="text-gray-dark h-12 space-y-6 rounded border-4 border-y border-white text-left"
                        >
                          <td className="border-bottom w-1/4 py-1">
                            <div className="flex">
                              <p className="text-gray-dark text-bold pr-2 text-base font-bold">
                                {moment(event.timestamp).format('ddd , MMMM Do YYYY, h:mm:ss a')}
                              </p>
                            </div>
                          </td>
                          <td className="border-bottom w-1/4 py-1">
                            <div className="flex">
                              <p className="text-gray-dark text-bold pr-2 text-base font-bold">
                                {event.event_category}
                              </p>
                            </div>
                          </td>
                          <td className="border-bottom w-1/4 py-1">
                            <div className="flex">
                              <p className="text-gray-dark text-bold pr-2 text-base font-bold">{event.event_name}</p>
                            </div>
                          </td>
                          <td className="border-bottom w-1/4 py-1">
                            <div className="flex">
                              <p className="text-gray-dark text-bold pr-2 text-base font-bold">{event.message}</p>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr
                        role="button"
                        className="text-gray-dark h-12 space-y-6 rounded border-4 border-y border-white text-left"
                      >
                        <td className="border-bottom w-1/2 py-1">
                          <div className="flex">
                            <p className="text-gray-dark text-bold pr-2 text-base font-bold">No Data Available</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

const selectClass =
  'max-w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

const Filters: React.FC<{
  page: number;
  count?: Count;
  limit: number;
  clients?: ClientMinimal[];
  domains?: { id: number; name: string }[];
  userState: 'live' | 'test';
  toggleUserState: () => void;
  setFilters: React.Dispatch<
    React.SetStateAction<{
      page: number;
      limit: number;
      search?: string;
      clientId?: number;
      domainId?: number;
    }>
  >;
}> = ({ page, count, limit, clients, domains, setFilters, userState, toggleUserState }) => {
  const pageSize = limit > 0 ? limit : 25;
  const total = count?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="gray-border-line bg-gray-background-light w-full shrink-0 border-b px-4 py-2 md:px-7">
      <div className="flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-4">
        <div className="min-h-[2.5rem] w-full min-w-[min(100%,18rem)] sm:min-w-[20rem] lg:min-w-[22rem] lg:max-w-2xl lg:flex-1">
          <Searchbar
            search={(data) => {
              setFilters((prev) => {
                return { ...prev, search: data ? data : undefined, page: 1 };
              });
            }}
          />
        </div>
        <div className="flex w-full flex-shrink-0 sm:w-auto">
          <PageJumpPagination
            idPrefix="calls"
            currentPage={page}
            pageCount={pageCount}
            totalCount={total}
            onPageChange={(p) => setFilters((prev) => ({ ...prev, page: p }))}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 lg:justify-end">
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-sm font-medium text-neutral-600">Test users</span>
            <Toggle
              checked={userState === 'test'}
              onChange={() => {
                toggleUserState();
              }}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
            <label className="whitespace-nowrap text-sm font-medium text-neutral-600" htmlFor="calls-filter-client">
              Member group
            </label>
            <select
              id="calls-filter-client"
              className={`${selectClass} w-40 sm:w-48`}
              onChange={(e) => {
                setFilters((prev) => {
                  return {
                    ...prev,
                    clientId: e.target.value ? parseInt(e.target.value) : undefined,
                    page: 1,
                  };
                });
              }}
            >
              <option value="">All groups</option>
              {clients?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
            <label className="whitespace-nowrap text-sm font-medium text-neutral-600" htmlFor="calls-filter-domain">
              Domain
            </label>
            <select
              id="calls-filter-domain"
              className={`${selectClass} w-40 sm:w-48`}
              onChange={(e) => {
                setFilters((prev) => {
                  return {
                    ...prev,
                    domainId: e.target.value ? parseInt(e.target.value) : undefined,
                    page: 1,
                  };
                });
              }}
            >
              <option value="">All domains</option>
              {domains?.map((item: { id: number; name: string }) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

const MemberTopics: React.FC<{ topics: string[] }> = ({ topics }): JSX.Element => {
  if (topics.length === 0) {
    return <p>No Topic</p>;
  }
  if (topics.length === 1) {
    return (
      <button className="bg-blue-gradient-1 cursor-auto whitespace-nowrap rounded-md px-3 py-2 text-center text-xs text-white">
        {topics[0]}
      </button>
    );
  }
  return (
    <div>
      <button className="bg-blue-gradient-1 mb-1 cursor-auto whitespace-nowrap rounded-md px-3 py-2 text-center text-xs text-white">
        {topics[0]}
      </button>
      <ToolTip text={`${topics.length - 1} more`} list={topics.slice(1)} />
    </div>
  );
};

const MemberNote: React.FC<{ text?: string; tooltipId: string }> = ({ text, tooltipId }): JSX.Element => {
  if (!text) {
    return <p>--</p>;
  }
  const truncated = text.length > 48;
  const preview = truncated ? `${text.slice(0, 48)}…` : text;
  if (!truncated) {
    return <p className="max-w-xs truncate text-xs">{preview}</p>;
  }
  return (
    <>
      <a data-tip data-for={tooltipId} className="block max-w-xs cursor-pointer truncate text-xs underline">
        {preview}
      </a>
      <ReactTooltip id={tooltipId} type="dark" place="bottom" effect="float" className="max-w-sm">
        <p className="whitespace-pre-wrap break-words text-xs">{text}</p>
      </ReactTooltip>
    </>
  );
};

const TopicsDiscussed: React.FC<{ topics: TopicTag[] }> = ({ topics }): JSX.Element => {
  const generateTopics = (topics: TopicTag[]) => {
    return topics.map((topic: TopicTag) => topic.tag.name);
  };

  if (topics?.length === 1) {
    return <p>{topics[0].tag.name}</p>;
  } else if (topics?.length > 1) {
    return (
      <div>
        {topics[0].tag.name} and <ToolTip text={`${topics.length - 1} more`} list={generateTopics(topics.slice(1))} />
      </div>
    );
  }
  return <p>--</p>;
};

const ToolTip: React.FC<{ text: string; list: string[] }> = ({ text, list }): JSX.Element => {
  return (
    <>
      {' '}
      <a data-tip data-for={list[0]} className="cursor-pointer underline">
        {text}
      </a>
      <ReactTooltip id={list[0]} type="dark" place="bottom" effect="float">
        <ul>
          {list.map((tag: string) => (
            <li>{tag}</li>
          ))}
        </ul>
      </ReactTooltip>
    </>
  );
};

export default Connections;
